/**
 * CoverageEligibilityRequest / CoverageEligibilityResponse helpers for VOB.
 */

import type {
  CodeableConcept,
  Coverage,
  CoverageEligibilityRequest,
  CoverageEligibilityResponse,
  CoverageEligibilityResponseInsuranceItem,
  Extension,
  Organization,
  Patient,
  Reference,
} from '@medplum/fhirtypes';

import {
  BENEFIT_NETWORK_SYSTEM,
  BENEFIT_TIER_EXT,
  BENEFIT_TYPE_SYSTEM,
  BILLING_SERVICE_CODE_SYSTEM,
  DEFAULT_REVERIFICATION_WINDOW_DAYS,
  EXTERNAL_VERIFICATION_ID_EXT,
  EXTERNAL_VERIFICATION_ID_IDENTIFIER_SYSTEM,
  EXTERNAL_VERIFICATION_TYPE_EXT,
  EXTERNAL_VERIFICATION_TYPE_SYSTEM,
  OOP_MAX_BENEFIT_TYPE_CODE,
  PLACE_OF_SERVICE_CODE_EXT,
  SOHAR_SPECIALTY_CODE_SYSTEM,
  UNIFY_VOB_BENEFIT_TYPE_SYSTEM,
  VERIFICATION_SOURCE_EXT,
  VERIFICATION_SOURCE_SYSTEM,
  VERIFIED_BILLING_SERVICE_CODE_EXT,
} from '../constants';

const USD = 'USD' as const;

export function usdMoney(value: number): { value: number; currency: typeof USD } {
  return { value, currency: USD };
}

export type BillingServiceCode = 'PSY' | 'IOP' | 'PCH';

export type VerificationSourceCode = 'automated' | 'manual';
export type ExternalVerificationTypeCode = 'sohar';

const MENTAL_HEALTH_CATEGORY: CodeableConcept = {
  coding: [
    {
      system: 'http://terminology.hl7.org/CodeSystem/ex-benefitcategory',
      code: 'mental-health',
      display: 'Mental Health',
    },
  ],
};

/** Map therapy service type → VOB billing service code (child-adolescent → PSY). */
export function therapyServiceTypeToBillingCode(serviceType: string | undefined): BillingServiceCode | undefined {
  if (!serviceType) return undefined;
  switch (serviceType) {
    case 'individual':
    case 'couples':
    case 'family':
    case 'group':
    case 'psych-testing':
    case 'child-adolescent':
      return 'PSY';
    case 'iop':
      return 'IOP';
    case 'medication-management':
      return 'PCH';
    default:
      return undefined;
  }
}

export function billingServiceCodeCoding(code: BillingServiceCode): CodeableConcept {
  const display =
    code === 'PSY' ? 'Psychotherapy' : code === 'IOP' ? 'Intensive Outpatient Program' : 'Psychiatry';
  return {
    coding: [{ system: SOHAR_SPECIALTY_CODE_SYSTEM, code, display }],
  };
}

export function unifyBillingServiceCodeCoding(code: BillingServiceCode): CodeableConcept {
  return {
    coding: [{ system: BILLING_SERVICE_CODE_SYSTEM, code, display: code }],
  };
}

function getExtension<T extends Extension>(
  resource: { extension?: Extension[] },
  url: string,
): T | undefined {
  return resource.extension?.find((e) => e.url === url) as T | undefined;
}

export function getExternalVerificationId(resource: CoverageEligibilityResponse): string | undefined {
  const identifier = resource.identifier?.find(
    (item) => item.system === EXTERNAL_VERIFICATION_ID_IDENTIFIER_SYSTEM,
  )?.value;
  if (identifier) return identifier;
  return getExtension(resource, EXTERNAL_VERIFICATION_ID_EXT)?.valueString;
}

export function getVerifiedBillingServiceCode(
  resource: CoverageEligibilityResponse,
): BillingServiceCode | undefined {
  const itemCode = resource.insurance
    ?.flatMap((insurance) => insurance.item ?? [])
    .flatMap((item) => item.productOrService?.coding ?? [])
    .find((coding) => coding.system === BILLING_SERVICE_CODE_SYSTEM)?.code;
  if (itemCode === 'PSY' || itemCode === 'IOP' || itemCode === 'PCH') return itemCode;
  const code = getExtension(resource, VERIFIED_BILLING_SERVICE_CODE_EXT)?.valueCoding?.code;
  if (code === 'PSY' || code === 'IOP' || code === 'PCH') return code;
  return undefined;
}

export function getVerificationSourceCode(
  resource: CoverageEligibilityResponse,
): VerificationSourceCode | undefined {
  return getExtension(resource, VERIFICATION_SOURCE_EXT)?.valueCoding?.code as
    | VerificationSourceCode
    | undefined;
}

export function insuranceComponentForCoverage(
  resource: CoverageEligibilityResponse,
  coverageRef?: string,
): NonNullable<CoverageEligibilityResponse['insurance']>[number] | undefined {
  const items = resource.insurance ?? [];
  if (coverageRef) {
    return items.find((item) => item.coverage?.reference === coverageRef);
  }
  return items.find((item) => item.coverage?.reference) ?? items[0];
}

export function getCoverageRefFromResponse(resource: CoverageEligibilityResponse): string | undefined {
  return insuranceComponentForCoverage(resource)?.coverage?.reference;
}

export function responseMatchesCoverage(
  resource: CoverageEligibilityResponse,
  coverageRef: string,
): boolean {
  return (resource.insurance ?? []).some((item) => item.coverage?.reference === coverageRef);
}

export function isResponseInReverificationWindow(
  resource: CoverageEligibilityResponse,
  windowDays: number = DEFAULT_REVERIFICATION_WINDOW_DAYS,
  nowMs: number = Date.now(),
): boolean {
  const created = resource.created;
  if (!created) return false;
  const createdMs = Date.parse(created);
  if (Number.isNaN(createdMs)) return false;
  const windowMs = windowDays * 24 * 60 * 60 * 1000;
  return nowMs - createdMs <= windowMs;
}

/** PRD valid VOB: active, complete, inforce, matching coverage + billing code, within window. */
export function isValidVobResponse(
  resource: CoverageEligibilityResponse,
  params: {
    coverageRef: string;
    billingCode: BillingServiceCode;
    windowDays?: number;
    nowMs?: number;
  },
): boolean {
  if (resource.status !== 'active') return false;
  if (resource.outcome !== 'complete') return false;
  if (insuranceComponentForCoverage(resource, params.coverageRef)?.inforce !== true) return false;
  if (!responseMatchesCoverage(resource, params.coverageRef)) return false;
  if (getVerifiedBillingServiceCode(resource) !== params.billingCode) return false;
  if (!isResponseInReverificationWindow(resource, params.windowDays, params.nowMs)) return false;
  return true;
}

export function buildCoverageEligibilityRequest(params: {
  patientRef: Reference<Patient>;
  insurerRef: Reference<Organization>;
  coverageRef: Reference<Coverage>;
  billingCode: BillingServiceCode;
  placeOfServiceCode?: string;
  externalVerificationType?: ExternalVerificationTypeCode;
  created?: string;
}): CoverageEligibilityRequest {
  const extensions: Extension[] = [];
  if (params.placeOfServiceCode) {
    extensions.push({ url: PLACE_OF_SERVICE_CODE_EXT, valueCode: params.placeOfServiceCode });
  }
  if (params.externalVerificationType) {
    extensions.push({
      url: EXTERNAL_VERIFICATION_TYPE_EXT,
      valueCoding: {
        system: EXTERNAL_VERIFICATION_TYPE_SYSTEM,
        code: params.externalVerificationType,
        display: 'Sohar',
      },
    });
  }
  return {
    resourceType: 'CoverageEligibilityRequest',
    status: 'active',
    purpose: ['benefits'],
    patient: params.patientRef,
    created: params.created ?? new Date().toISOString(),
    insurer: params.insurerRef,
    insurance: [{ coverage: params.coverageRef }],
    item: [
      {
        category: MENTAL_HEALTH_CATEGORY,
        productOrService: billingServiceCodeCoding(params.billingCode),
      },
    ],
    ...(extensions.length ? { extension: extensions } : {}),
  };
}

export function buildQueuedCoverageEligibilityResponse(params: {
  patientRef: Reference<Patient>;
  insurerRef: Reference<Organization>;
  coverageRef: Reference<Coverage>;
  requestRef: Reference<CoverageEligibilityRequest>;
  externalVerificationId: string;
  externalVerificationType?: ExternalVerificationTypeCode;
  billingCode: BillingServiceCode;
  disposition?: string;
}): CoverageEligibilityResponse {
  return {
    resourceType: 'CoverageEligibilityResponse',
    status: 'active',
    purpose: ['benefits'],
    outcome: 'queued',
    patient: params.patientRef,
    created: new Date().toISOString(),
    insurer: params.insurerRef,
    request: params.requestRef,
    disposition: params.disposition ?? 'Verification pending',
    extension: [
      {
        url: EXTERNAL_VERIFICATION_TYPE_EXT,
        valueCoding: {
          system: EXTERNAL_VERIFICATION_TYPE_SYSTEM,
          code: params.externalVerificationType ?? 'sohar',
        },
      },
      {
        url: VERIFICATION_SOURCE_EXT,
        valueCoding: { system: VERIFICATION_SOURCE_SYSTEM, code: 'automated', display: 'Automated' },
      },
    ],
    insurance: [
      {
        coverage: params.coverageRef,
        item: [{ productOrService: unifyBillingServiceCodeCoding(params.billingCode) }],
      },
    ],
    identifier: [
      {
        system: EXTERNAL_VERIFICATION_ID_IDENTIFIER_SYSTEM,
        value: params.externalVerificationId,
      },
    ],
  };
}

export function buildInsuranceItemsFromBenefits(
  benefits: Array<{
    network: 'in' | 'out';
    copay?: number;
    coinsurance?: number;
    deductibleTotal?: number;
    deductibleRemaining?: number;
    deductibleCoverageLevel?: string;
    oopTotal?: number;
    oopRemaining?: number;
    oopCoverageLevel?: string;
    visitTotal?: number;
    visitRemaining?: number;
  }>,
  billingCode: BillingServiceCode,
): CoverageEligibilityResponseInsuranceItem[] {
  // ces-1: response insurance items SHALL have category xor productOrService.
  // We keep the specific billing code; mental-health category stays on the request item.
  const buildItem = (
    b: (typeof benefits)[number],
    benefit: NonNullable<CoverageEligibilityResponseInsuranceItem['benefit']>,
    unit?: 'individual' | 'family',
  ): CoverageEligibilityResponseInsuranceItem => ({
    productOrService: unifyBillingServiceCodeCoding(billingCode),
    network: {
      coding: [
        b.network === 'out'
          ? { system: BENEFIT_NETWORK_SYSTEM, code: 'out', display: 'Out of Network' }
          : { system: BENEFIT_NETWORK_SYSTEM, code: 'in', display: 'In Network' },
      ],
    },
    ...(unit
      ? {
          unit: {
            coding: [
              {
                system: 'http://terminology.hl7.org/CodeSystem/benefit-unit',
                code: unit,
              },
            ],
          },
        }
      : {}),
    benefit,
  });
  const coverageLevel = (value: string | undefined): 'individual' | 'family' | undefined => {
    if (value === 'IND') return 'individual';
    if (value === 'FAM') return 'family';
    return undefined;
  };

  return benefits.flatMap((b) => {
    const unqualified = [
      ...(b.copay !== undefined
        ? [
            {
              type: { coding: [{ system: BENEFIT_TYPE_SYSTEM, code: 'copay', display: 'Copayment per service' }] },
              allowedMoney: usdMoney(b.copay),
            },
          ]
        : []),
      ...(b.coinsurance !== undefined
        ? [
            {
              type: {
                coding: [
                  { system: BENEFIT_TYPE_SYSTEM, code: 'copay-percent', display: 'Copayment Percent per service' },
                ],
              },
              allowedUnsignedInt: b.coinsurance,
            },
          ]
        : []),
      ...(b.visitTotal !== undefined
        ? [
            {
              type: { coding: [{ system: BENEFIT_TYPE_SYSTEM, code: 'visit', display: 'Visit' }] },
              allowedUnsignedInt: b.visitTotal,
              ...(b.visitRemaining !== undefined
                ? { usedUnsignedInt: b.visitTotal - b.visitRemaining }
                : {}),
            },
          ]
        : []),
    ];
    const deductible =
      b.deductibleTotal !== undefined
        ? [
            {
              type: { coding: [{ system: BENEFIT_TYPE_SYSTEM, code: 'deductible', display: 'Deductible' }] },
              allowedMoney: usdMoney(b.deductibleTotal),
              ...(b.deductibleRemaining !== undefined
                ? { usedMoney: usdMoney(b.deductibleTotal - b.deductibleRemaining) }
                : {}),
            },
          ]
        : [];
    const oop =
      b.oopTotal !== undefined
        ? [
            {
              type: {
                coding: [
                  {
                    system: UNIFY_VOB_BENEFIT_TYPE_SYSTEM,
                    code: OOP_MAX_BENEFIT_TYPE_CODE,
                    display: 'Out of Pocket Maximum',
                  },
                ],
              },
              allowedMoney: usdMoney(b.oopTotal),
              ...(b.oopRemaining !== undefined
                ? { usedMoney: usdMoney(b.oopTotal - b.oopRemaining) }
                : {}),
            },
          ]
        : [];
    const deductibleUnit = deductible.length
      ? coverageLevel(b.deductibleCoverageLevel)
      : undefined;
    const oopUnit = oop.length ? coverageLevel(b.oopCoverageLevel) : undefined;
    if (deductibleUnit && oopUnit && deductibleUnit !== oopUnit) {
      return [
        ...(deductible.length ? [buildItem(b, deductible, deductibleUnit)] : []),
        ...(oop.length ? [buildItem(b, oop, oopUnit)] : []),
        ...(unqualified.length ? [buildItem(b, unqualified)] : []),
      ];
    }
    return [buildItem(b, [...unqualified, ...deductible, ...oop], deductibleUnit ?? oopUnit)];
  });
}

export function applyFinalVobToResponse(
  resource: CoverageEligibilityResponse,
  params: {
    outcome: 'complete' | 'error' | 'partial';
    inforce?: boolean;
    disposition?: string;
    billingCode: BillingServiceCode;
    verificationSource: VerificationSourceCode;
    items?: CoverageEligibilityResponse['insurance'];
    externalVerificationId?: string;
    externalVerificationType?: ExternalVerificationTypeCode;
  },
): CoverageEligibilityResponse {
  const extensions: Extension[] = (resource.extension ?? []).filter(
    (e) =>
      e.url !== VERIFICATION_SOURCE_EXT &&
      e.url !== VERIFIED_BILLING_SERVICE_CODE_EXT &&
      e.url !== EXTERNAL_VERIFICATION_ID_EXT,
  );
  extensions.push({
    url: VERIFICATION_SOURCE_EXT,
    valueCoding: {
      system: VERIFICATION_SOURCE_SYSTEM,
      code: params.verificationSource,
      display: params.verificationSource === 'manual' ? 'Manual' : 'Automated',
    },
  });
  if (params.verificationSource === 'automated' && params.externalVerificationType) {
    const hasType = extensions.some((e) => e.url === EXTERNAL_VERIFICATION_TYPE_EXT);
    if (!hasType) {
      extensions.push({
        url: EXTERNAL_VERIFICATION_TYPE_EXT,
        valueCoding: {
          system: EXTERNAL_VERIFICATION_TYPE_SYSTEM,
          code: params.externalVerificationType,
        },
      });
    }
  }

  const insurance = (
    params.items ??
    (params.inforce !== undefined && resource.insurance?.[0]
      ? [{ ...resource.insurance[0], inforce: params.inforce }]
      : resource.insurance)
  )?.map((component) => ({
    ...component,
    item:
      component.item?.map((item) => ({
        ...item,
        productOrService: item.productOrService ?? unifyBillingServiceCodeCoding(params.billingCode),
      })) ?? [{ productOrService: unifyBillingServiceCodeCoding(params.billingCode) }],
  }));

  const identifier = params.externalVerificationId
    ? [
        ...(resource.identifier ?? []).filter(
          (item) => item.system !== EXTERNAL_VERIFICATION_ID_IDENTIFIER_SYSTEM,
        ),
        {
          system: EXTERNAL_VERIFICATION_ID_IDENTIFIER_SYSTEM,
          value: params.externalVerificationId,
        },
      ]
    : resource.identifier;

  return {
    ...resource,
    outcome: params.outcome,
    disposition: params.disposition ?? resource.disposition,
    extension: extensions,
    insurance,
    identifier,
  };
}

export function coverageIsActiveForVob(coverage: Coverage): boolean {
  return coverage.status === 'active';
}

export { BENEFIT_TIER_EXT, MENTAL_HEALTH_CATEGORY };
